import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, inject } from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormGroupDirective,
  Validators,
  FormGroup,
} from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { combineLatest, EMPTY, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  startWith,
  filter,
  switchMap,
  takeUntil,
  withLatestFrom,
  debounceTime,
} from 'rxjs/operators';
import { Admission, CropAid, FertilizerTypeEnum } from 'src/app/crop-aid/crop-aid.model';

import * as CropAidSelectors from 'src/app/crop-aid/store/crop-aid.selectors';
import * as FertilizationLegalStandardsSelectors from 'src/app/fertilization-legal-standards/store/fertilization-legal-standard.selectors';
import * as SalesOrderPeriodSelectors from 'src/app/sales-order-period/store/sales-order-period.selectors';
import { SiteState } from 'src/app/shared/models';
import { FertilizationLegalStandardApiService } from 'src/app/shared/services/fertilization-legal-standard-api.service';
import * as SoilTypeSelectors from 'src/app/soil-type/store/soil-type.selectors';
import * as AppSelectors from 'src/app/store/selectors';

import { FertilizerForm, FertilizerType } from '../models/fertilizer.model';

/**
 * Input forms for a fertilizer
 * Updates and deleting is handled by the parent component
 * There is a setting for batch edit, this setting does the following:
 * - Don't reset fields on crop aid change
 * - Don't hide input fields when crop aid is empty
 * - Dispatch delete when the fertilizer hase not been posted
 * */
@Component({
  selector: 'app-fertilizer-form',
  templateUrl: './fertilizer-form.component.html',
  styleUrls: ['fertilizer-form.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  standalone: false,
})
export class FertilizerFormComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly fertilizationLegalStandardApi = inject(FertilizationLegalStandardApiService);

  private readonly destroyed$ = new Subject<boolean>();
  private readonly cropAids$ = this.store.select(CropAidSelectors.selectCropAidFertilizers);
  private readonly cropYear$ = this.store.select(AppSelectors.getCropYear);
  private readonly nManureBalance$ = this.store.pipe(
    select(FertilizationLegalStandardsSelectors.selectNManureBalance),
  );
  private readonly nTotalBalance$ = this.store.pipe(
    select(FertilizationLegalStandardsSelectors.selectNTotalBalance),
  );
  private readonly p2O5Balance$ = this.store.pipe(
    select(FertilizationLegalStandardsSelectors.selectP2O5Balance),
  );
  @Output() public readonly deleteFertilizer = new EventEmitter<FormGroup>();
  @Output() public readonly updateFertilizer = new EventEmitter<FormGroup>();
  private cropAids: CropAid[] = [];
  @Input() public formGroup: FormGroup;
  @Input() public isBatchEdit = false;
  @Input() public showDeleteButton = true;
  @Input() public isLoading = false;
  public readonly soilTypes$ = this.store.select(SoilTypeSelectors.selectSoilTypes);
  public readonly salesOrderPeriods$ = this.store.pipe(
    select(SalesOrderPeriodSelectors.selectSalesOrderPeriods),
  );
  public isFertilizerForm = false;
  public isNotDelivered = false;
  public isAnimal = false;
  public filteredProducts: CropAid[] = [];
  public showComment = false;
  public iconName = 'warning';
  public iconStyle: string | undefined = undefined;
  public iconColor = 'warn';
  public unitOfMeasurementDisplay = '';

  private filterProducts(value: string, cropAids: CropAid[]): CropAid[] {
    const filterValue = value.toLowerCase();
    return cropAids
      .filter((option) => option.isActive)
      .filter((option) => option.name.toLowerCase().includes(filterValue));
  }

  private setNutritionalValues(cropAids: CropAid[]) {
    if (this.isBatchEdit) {
      return;
    }
    const cropAidId = this.formGroup.get('cropAidId').value;
    const cropAid = cropAids.find((option) => option.id === cropAidId);
    const dosage = this.formGroup.get('dosage').value;
    const totalNitrogenControl = this.formGroup.get('totalNitrogen');
    const totalPhosphateControl = this.formGroup.get('totalPhosphate');
    if (cropAid && dosage) {
      totalNitrogenControl.setValue(cropAid.fertilizer.nutritionalComposition.n * dosage, {
        emitEvent: false,
      });
      totalPhosphateControl.setValue(cropAid.fertilizer.nutritionalComposition.p2O5 * dosage, {
        emitEvent: false,
      });
    }
    this.formGroup.updateValueAndValidity();
  }

  private updateIsAnimalStatus(cropAidId: number, cropAids: CropAid[]) {
    const cropAid = cropAids.find((option) => option.id === cropAidId);
    this.isAnimal = cropAid?.fertilizer.fertilizerType === FertilizerTypeEnum.Animal;
  }

  private setProduct() {
    if (this.isAnimal) {
      this.formGroup.get('soilTypeId').setValidators([Validators.required, Validators.min(1)]);
      this.formGroup.get('periodId').setValidators([Validators.required, Validators.min(1)]);
    } else {
      this.formGroup.get('soilTypeId').clearValidators();
      this.formGroup.get('periodId').clearValidators();
    }
    this.formGroup.get('soilTypeId').updateValueAndValidity();
    this.formGroup.get('periodId').updateValueAndValidity();
    this.formGroup.get('comment').updateValueAndValidity();
  }

  private clearProduct() {
    this.formGroup.get('cropAidId').setValue(null, { emitEvent: false });
    this.formGroup.get('dosage').setValue(null, { emitEvent: false });
    this.formGroup.get('totalNitrogen').setValue(null, { emitEvent: false });
    this.formGroup.get('totalPhosphate').setValue(null, { emitEvent: false });
    this.formGroup.get('soilTypeId').setValue(null, { emitEvent: false });
    this.formGroup.get('comment').setValue(null, { emitEvent: false });

    this.isAnimal = false;
    this.formGroup.updateValueAndValidity();
  }

  ngOnInit() {
    const fertilizerType = this.formGroup.get('fertilizerType').value;
    this.isFertilizerForm =
      fertilizerType === FertilizerType.Delivered || fertilizerType === FertilizerType.NotDelivered;
    this.isNotDelivered = fertilizerType === FertilizerType.NotDelivered;

    this.cropAids$.pipe(takeUntil(this.destroyed$)).subscribe((cropAids) => {
      this.cropAids = cropAids;
    });

    combineLatest([
      this.formGroup.get('cropAidId').valueChanges.pipe(
        startWith(''),
        filter((value) => typeof value === 'string'),
        takeUntil(this.destroyed$),
      ),
      this.cropAids$,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([value, cropAids]) => {
        this.filteredProducts = this.filterProducts(value, cropAids);
      });

    combineLatest([
      this.formGroup.get('cropAidId').valueChanges.pipe(
        startWith(this.formGroup.get('cropAidId').value),
        filter((value) => value && typeof value !== 'string'),
        takeUntil(this.destroyed$),
      ),
      this.cropAids$,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([value, cropAids]) => {
        this.updateIsAnimalStatus(value, cropAids);
        this.setProduct();
        this.updateUnitOfMeasurementDisplay();
      });

    if (this.formGroup.get('fertilizerType').value === FertilizerType.NotDelivered) {
      combineLatest([
        this.formGroup.get('cropAidId').valueChanges,
        this.formGroup.get('periodId').valueChanges,
        this.formGroup.get('soilTypeId').valueChanges,
      ])
        .pipe(
          filter(([cropAidId]) => cropAidId),
          withLatestFrom(
            this.cropYear$,
            this.nManureBalance$,
            this.nTotalBalance$,
            this.p2O5Balance$,
          ),
          takeUntil(this.destroyed$),
          switchMap(
            ([
              [cropAidId, periodId, soilTypeId],
              cropYear,
              nManureBalance,
              nTotalBalance,
              p2O5Balance,
            ]) => {
              const cropAidControl = this.formGroup.get('cropAidId');
              const periodIdControl = this.formGroup.get('periodId');
              const soilTypeIdControl = this.formGroup.get('soilTypeId');
              const dosageControl = this.formGroup.get('dosage');
              if (
                !!cropAidId &&
                nManureBalance &&
                nTotalBalance &&
                p2O5Balance &&
                !dosageControl.valid &&
                cropAidControl.valid &&
                periodIdControl.valid &&
                soilTypeIdControl.valid
              ) {
                return this.fertilizationLegalStandardApi.getOptimizedFertilizer({
                  cropYear,
                  nManureBalance,
                  nTotalBalance,
                  p2O5Balance,
                  fertilizer: {
                    cropAidId,
                    periodId,
                    soilTypeId,
                  },
                });
              } else {
                return EMPTY;
              }
            },
          ),
        )
        .subscribe((value) => {
          if (value.allowedDosage) {
            this.formGroup.get('dosage').setValue(value.allowedDosage);
          }
        });
    }

    combineLatest([
      this.formGroup.get('dosage').valueChanges.pipe(takeUntil(this.destroyed$)),
      this.cropAids$,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([_value, cropAids]) => {
        this.setNutritionalValues(cropAids);
      });

    combineLatest([
      this.formGroup.get('cropAidId').valueChanges.pipe(takeUntil(this.destroyed$)),
      this.cropAids$,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([_value, cropAids]) => {
        this.setNutritionalValues(cropAids);
      });

    this.formGroup.valueChanges
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntil(this.destroyed$),
        debounceTime(1000),
        switchMap((_: [FertilizerForm]) => {
          const fertilizer: FertilizerForm = this.formGroup.value;
          if (!this.formGroup.valid || typeof fertilizer.cropAidId === 'string') {
            return EMPTY;
          }
          this.updateFertilizer.emit(this.formGroup);
          return of();
        }),
      )
      .subscribe(() => {});

    this.formGroup
      .get('comment')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((value) => (this.showComment = !!value));

    this.showComment = this.hasComment();

    this.updateIconState();
    this.formGroup.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.updateIconState();
    });

    this.updateUnitOfMeasurementDisplay();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  delete() {
    if (this.formGroup.get('fertilizerId').value || this.isBatchEdit) {
      this.deleteFertilizer.emit(this.formGroup);
    }
  }

  canFertilizerMove(): boolean {
    const fertilizer: FertilizerForm = this.formGroup.value;
    return this.formGroup.valid && !this.isLoading && !!fertilizer?.fertilizerId;
  }

  moveToDelivered() {
    if (!this.canFertilizerMove()) {
      return;
    }
    const fertilizer: FertilizerForm = this.formGroup.value;
    fertilizer.fertilizerType = FertilizerType.Delivered;
    this.updateFertilizer.emit(this.formGroup);
  }

  checkProduct() {
    if (typeof this.formGroup.get('cropAidId').value === 'string' && !this.isBatchEdit) {
      this.clearProduct();
    }
  }

  findProduct(cropAidId: number): CropAid {
    return this.cropAids.find((cropAid) => cropAid.id === cropAidId);
  }

  displayProduct() {
    return (cropAidId?: number) => (cropAidId ? this.findProduct(cropAidId)?.name : '');
  }

  displayUnitOfMeasurement(control: AbstractControl) {
    const cropAid = this.cropAids.find((item) => item.id === control.value);
    return cropAid ? `(${cropAid.unitOfMeasurement})` : '';
  }

  getAdmissionNumberString(admissions: Admission[]) {
    if (!admissions || admissions.length < 1) {
      return;
    }

    return `(${admissions.map((admission) => admission.number).join(', ')})`;
  }

  hasComment = (): boolean => !!this.formGroup.get('comment').value?.trim();

  private updateIconState(): void {
    this.iconName = this.getIconName();
    this.iconStyle = this.getIconStyle();
    this.iconColor = this.getIconColor();
  }

  private updateUnitOfMeasurementDisplay(): void {
    const cropAidControl = this.formGroup.get('cropAidId');
    const cropAid = this.cropAids.find((item) => item.id === cropAidControl.value);
    this.unitOfMeasurementDisplay = cropAid ? `(${cropAid.unitOfMeasurement})` : '';
  }

  getIconName = (): string => {
    if (this.formGroup.valid) {
      switch (this.formGroup.get('fertilizerType').value) {
        case FertilizerType.Delivered:
          return 'check';
        case FertilizerType.NotDelivered:
        case FertilizerType.Dispatched:
          return 'local_shipping';
        case FertilizerType.InitialStock:
          return 'archive';
        case FertilizerType.ClosingStock:
          return 'unarchive';
        default:
          return 'device_unknown';
      }
    } else {
      return 'warning';
    }
  };

  getIconStyle = (): string => {
    if (
      this.formGroup.valid &&
      this.formGroup.get('fertilizerType').value === FertilizerType.Dispatched
    ) {
      return 'transform: scaleX(-1)';
    }
  };

  getIconColor = (): string => (this.formGroup.valid ? 'primary' : 'warn');
}
